import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Helper to format API errors
const formatApiError = (error) => {
  const detail = error.response?.data?.detail;
  if (!detail) return error.message || 'An error occurred';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  }
  return String(detail);
};

// Token storage
const TOKEN_KEY = 'noor_albayan_token';

const getStoredToken = () => localStorage.getItem(TOKEN_KEY);
const setStoredToken = (token) => localStorage.setItem(TOKEN_KEY, token);
const clearStoredToken = () => localStorage.removeItem(TOKEN_KEY);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not logged in
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setUser(response.data);
    } catch {
      clearStoredToken();
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Handle forced session expiry fired by the API interceptor (token refresh
  // failed). We must NOT use window.location.href because that triggers a full
  // WebView reload in Capacitor, causing a reload loop. Instead we update
  // local state; React Router guards will redirect to /login automatically.
  useEffect(() => {
    const handleSessionExpired = () => {
      clearStoredToken();
      setUser(false);
    };
    window.addEventListener('noor:auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('noor:auth:session-expired', handleSessionExpired);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, 
        { email, password },
        { withCredentials: true }
      );
      const data = response.data;
      if (data.token) {
        setStoredToken(data.token);
      }
      setUser(data);
      return { success: true, user: data };
    } catch (error) {
      return { success: false, error: formatApiError(error) };
    }
  };

  const register = async (payload) => {
    // payload may be either the legacy positional shape (name,email,password)
    // or the new object shape: { name, email, password, role, madrassa_id?, madrassa_info?, invitation_token? }
    try {
      const body = typeof payload === 'string'
        ? { name: payload, email: arguments[1], password: arguments[2], role: 'student' }
        : payload;
      const response = await axios.post(`${API_URL}/api/auth/register`,
        body,
        { withCredentials: true }
      );
      const data = response.data;
      if (data.token) {
        setStoredToken(data.token);
      }
      setUser(data);
      return { success: true, user: data };
    } catch (error) {
      return { success: false, error: formatApiError(error) };
    }
  };

  const logout = async () => {
    try {
      const token = getStoredToken();
      await axios.post(`${API_URL}/api/auth/logout`, {}, { 
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true 
      });
    } catch {
      // Ignore errors
    }
    clearStoredToken();
    setUser(false);
  };

  // Refresh the current user (e.g. after updating preferences)
  const refreshUser = useCallback(async () => {
    const token = getStoredToken();
    if (!token) return null;
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      setUser(response.data);
      return response.data;
    } catch {
      return null;
    }
  }, []);

  // Update user tafsir preferences
  const updatePreferences = async (prefs) => {
    const token = getStoredToken();
    if (!token) return { success: false, error: 'Not authenticated' };
    try {
      const response = await axios.patch(
        `${API_URL}/api/auth/me/preferences`,
        prefs,
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        },
      );
      // Merge back into user
      setUser((prev) => ({
        ...(prev || {}),
        preferences: {
          ...((prev && prev.preferences) || {}),
          tafsir: response.data,
        },
      }));
      return { success: true, tafsir: response.data };
    } catch (error) {
      return { success: false, error: formatApiError(error) };
    }
  };

  // Helper to get auth headers for API calls
  const getAuthHeaders = () => {
    const token = getStoredToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider value={{ user, loading, isAuthenticated: !!user, login, register, logout, checkAuth, refreshUser, updatePreferences, getAuthHeaders }}>
      {children}
    </AuthContext.Provider>
  );
};
